/**
 * Utility functions for API operations
 */

import { makeApiCall } from './nativeApiUtils';

/**
 * Retrieves the API base URL directly from the environment variable.
 * It is expected that REACT_APP_API_BASE_URL is set in the .env file.
 * @returns {string} The base API URL
 * @throws {Error} If REACT_APP_API_BASE_URL is not set.
 */
export const getApiBaseUrl = () => {
  const useNginxProxy = process.env.REACT_APP_USE_NGINX_PROXY === 'true';
  const apiBackendBaseUrl = process.env.REACT_APP_MIRASOL_BACKEND_URL;

  // Log for debugging purposes during development
  // if (process.env.NODE_ENV !== 'production') {
  //   console.log('REACT_APP_USE_NGINX_PROXY:', process.env.REACT_APP_USE_NGINX_PROXY);
  //   console.log('REACT_APP_MIRASOL_BACKEND_URL:', apiBackendBaseUrl);
  //   console.log('Using Nginx Proxy:', useNginxProxy);
  // }

  if (useNginxProxy) {
    // When using Nginx proxy, the base URL is just '/api'
    return '/api';
  } else {
    // Otherwise, use the full backend URL from environment variable
    if (!apiBackendBaseUrl) {
      // For a production application, a missing backend API base URL is a critical configuration error.
      throw new Error('CRITICAL CONFIGURATION ERROR: REACT_APP_MIRASOL_BACKEND_URL environment variable is not set. API calls cannot be made.');
    }
    return apiBackendBaseUrl;
  }
};

/**
 * Constructs a full API URL by combining the base URL with the endpoint path.
 * @param {string} endpoint - The API endpoint path (e.g., '/rewrite-section/')
 * @returns {string} The complete API URL
 */
export const getApiUrl = (endpoint) => {
  const baseUrl = getApiBaseUrl();
  // Ensure endpoint starts with '/api/'
  // If the provided endpoint already correctly starts with /api/ or is just a base path,
  // this logic might need adjustment based on how backend routes are structured.
  // Assuming endpoint might be like "/users" or "users" and needs "/api" prefix.
  let apiEndpoint = endpoint;
  if (!endpoint.startsWith('/api/')) {
    apiEndpoint = `/api${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
  }
  
  return `${baseUrl}${apiEndpoint}`;
};

/**
 * Performs a fetch request to a backend API endpoint, automatically adding the Sol API key header.
 * @param {string} endpoint - The API endpoint path (e.g., '/rewrite-section/')
 * @param {object} [options={}] - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} The fetch Response object
 * @throws {Error} If REACT_APP_SOL_API_KEY is not set.
 */
export const fetchBackendApi = async (endpoint, options = {}) => {
  const apiUrl = getApiUrl(endpoint);
  const solApiKey = process.env.REACT_APP_SOL_API_KEY; // Get Sol API key from environment variables

  if (!solApiKey) {
    console.error('[apiUtils] API_KEY is not set. Cannot make backend API call.');
    throw new Error('REACT_APP_SOL_API_KEY not configured.');
  }

  const headers = {
    'X-Sol-API-Key': solApiKey, // Add Sol API key header
    ...options.headers, // Merge with any existing headers
  };

  const fetchOptions = {
    ...options,
    headers,
  };

  return makeApiCall(apiUrl, fetchOptions);
};

/**
 * Performs a fetch request to a backend API endpoint using Bearer token authentication.
 * @param {string} endpoint - The API endpoint path (e.g., '/provider/initiate-visit/')
 * @param {string} bearerToken - The Bearer token for authentication
 * @param {object} [options={}] - Fetch options (method, headers, body, etc.)
 * @returns {Promise<Response>} The fetch Response object
 * @throws {Error} If bearerToken is not provided.
 */
export const fetchBackendApiWithBearer = async (endpoint, bearerToken, options = {}) => {
  const apiUrl = getApiUrl(endpoint);

  if (!bearerToken) {
    console.error('[apiUtils] Bearer token is not provided. Cannot make backend API call.');
    throw new Error('Bearer token not provided.');
  }

  const headers = {
    'Authorization': `Bearer ${bearerToken}`,
    'Content-Type': 'application/json',
    ...options.headers, // Merge with any existing headers
  };

  const fetchOptions = {
    ...options,
    headers,
  };

  return makeApiCall(apiUrl, fetchOptions);
};

/**
 * Checks if there is a valid parent window context for postMessage communication.
 * A valid context exists if the current window is in an iframe and the parent origin
 * is defined in environment variables.
 *
 * @returns {boolean} True if a valid parent window context is available, false otherwise.
 */
export const isParentWindowAvailable = () => {
  return window.parent !== window && process.env.REACT_APP_EMBED_ALLOWED_ORIGINS;
};

/**
 * Gets the defined parent window origin from environment variables.
 *
 * @returns {string | undefined} The parent window origin string, or undefined if not set.
 */
export const getParentWindowOrigin = () => {
  return process.env.REACT_APP_EMBED_ALLOWED_ORIGINS;
};

/**
 * Sends a message to the parent window if a valid parent window context is available.
 *
 * @param {string} type The type of the message (e.g., 'sessionStarted', 'zoomError').
 * @param {object} payload The payload data to send with the message.
 */
export const sendMessageToParent = (type, payload) => {
  if (isParentWindowAvailable()) {
    try {
      window.parent.postMessage({ type, payload }, getParentWindowOrigin());
      console.log(`[apiUtils] Message sent to parent: type=${type}, payload=`, payload);
    } catch (error) {
      console.error(`[apiUtils] Error sending message to parent: type=${type}, error=`, error);
    }
  } else {
    console.log(`[apiUtils] Not sending message to parent (context not available): type=${type}, payload=`, payload);
  }
};

/**
 * Constructs a WebSocket URL from the backend URL
 * @param {string} path - The WebSocket path (e.g., '/ws/audio/zoom/123/')
 * @returns {string} The complete WebSocket URL
 */
export const getWebSocketUrl = (path) => {
  const backendUrl = process.env.REACT_APP_MIRASOL_BACKEND_URL || '';
  const wsProtocol = backendUrl.startsWith('https') ? 'wss' : 'ws';
  const wsHost = backendUrl.replace(/^https?:\/\//, '').replace(/\/$/, ''); // Remove protocol and trailing slash
  return `${wsProtocol}://${wsHost}${path}`;
};